import { useState, useEffect } from "react";
import { Row, Col, Form, Button, Spinner } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import AsyncSelect from 'react-select/async'; 
import api from "../../../services/api";
import Swal from 'sweetalert2';
import { FaSave } from "react-icons/fa";
import { getCurrentUser } from '../../../common/utils';

const OcorrenciasEdit = ({ id, programa_id, onSuccess, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [dropdowns, setDropdowns] = useState({
        origens: [],
        vias: [],
        doencas: []
    });

    const { register, handleSubmit, setValue, control, formState: { errors } } = useForm();
    const dados_usuario = getCurrentUser();
    const currentProgramaId = programa_id || dados_usuario['ambiente_programa_id'];

    useEffect(() => {
        if (currentProgramaId) {
            carregarDropdowns(currentProgramaId);
        }
        if (id) {
            carregarDadosEdicao(id);
        }
    }, [id, currentProgramaId]);

    const carregarDropdowns = async (progId) => {
        try {
            const [respOrigens, respVias, respDoencas] = await Promise.all([
                api.get(`/visitas/origens/?programa_id=${progId}`),
                api.get(`/visitas/vias/?programa_id=${progId}`),
                api.get(`/programas/doencas/?programa_id=${progId}`) 
            ]);

            const listaDoencas = (respDoencas.data.results || respDoencas.data).map(item => ({
                id: item.dados_doenca.id,
                nome: item.dados_doenca.nome
            }));

            setDropdowns({
                origens: respOrigens.data,
                vias: respVias.data,
                doencas: listaDoencas
            });
        } catch (error) {
            console.error("Erro ao carregar opções", error);
        }
    };

    const carregarMedicos = async (inputValue) => {
        if (!currentProgramaId) return [];
        try {
            const url = `/pessoas/prestadores/?nome=${inputValue}&programa_id=${currentProgramaId}&categoria=medicos|multiprofissionais&is_active=1&tipo_usuario=${dados_usuario.tipo_usuario}`;
            const response = await api.get(url);
            
            return response.data.results.map(medico => ({
                value: medico.id,
                label: `${medico.nome} (${medico.registro || 'S/CRM'})`
            }));
        } catch (error) {
            console.error("Erro ao buscar médicos", error);
            return [];
        }
    };

    const carregarDadosEdicao = async (registroId) => {
        try {
            const response = await api.get(`/ocorrencias/${registroId}/`);
            const dados = response.data;
            
            if (dados.medico) {
                setValue('medico', { value: dados.medico, label: dados.medico_nome });
            }

            setValue('data', dados.data ? dados.data.substring(0, 16) : ''); 
            setValue('origem', dados.origem);
            setValue('via', dados.via);
            setValue('descricao', dados.descricao);
            setValue('patologia', dados.patologia);
            setValue('observacao', dados.observacao);
        } catch (error) {
            console.error("Erro ao carregar registro", error);
            Swal.fire('Erro', 'Não foi possível carregar os dados.', 'error');
        }
    };

    const onSubmit = async (data) => {
        setLoading(true);
        const payload = { 
            ...data, 
            medico: data.medico?.value, 
            programa: currentProgramaId 
        };

        try {
            if (id) {
                await api.put(`/ocorrencias/${id}/`, payload);
                Swal.fire('', '', 'success');
            } else {
                await api.post('/ocorrencias/', payload);
                Swal.fire('', '', 'success');
            }
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error(error);
            Swal.fire('Erro', 'Erro ao salvar o registro.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Médico <span className="text-danger">*</span></Form.Label>
                        <Controller
                            name="medico"
                            control={control}
                            rules={{ required: "Selecione um médico" }}
                            render={({ field }) => (
                                <AsyncSelect
                                    {...field}
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={carregarMedicos}
                                    placeholder="Digite o nome ou CRM..."
                                    noOptionsMessage={() => "Nenhum médico encontrado"}
                                    classNamePrefix="select"
                                />
                            )}
                        />
                        {errors.medico && <span className="text-danger small">{errors.medico.message}</span>}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Data da Tentativa <span className="text-danger">*</span></Form.Label>
                        <Form.Control type="datetime-local" {...register("data", { required: true })} isInvalid={!!errors.data} />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Origem do Contato <span className="text-danger">*</span></Form.Label>
                        <Form.Select {...register("origem", { required: true })} isInvalid={!!errors.origem}>
                            <option value="">Selecione...</option>
                            {dropdowns.origens.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Via de Contato <span className="text-danger">*</span></Form.Label>
                        <Form.Select {...register("via", { required: true })} isInvalid={!!errors.via}>
                            <option value="">Selecione...</option>
                            {dropdowns.vias.map(v => <option key={v.id} value={v.id}>{v.nome}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Patologia (Opcional)</Form.Label>
                        <Form.Select {...register("patologia")}>
                            <option value="">Selecione...</option>
                            {dropdowns.doencas.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Descrição (Padrão) <span className="text-danger">*</span></Form.Label>
                        <Form.Control as="textarea" rows={3} {...register("descricao", { required: true })} placeholder="Descreva o que ocorreu..." isInvalid={!!errors.descricao} />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Observação (Interna)</Form.Label>
                        <Form.Control as="textarea" rows={2} {...register("observacao")} />
                    </Form.Group>
                </Col>
            </Row>
            
            <div className="text-end border-top pt-3 mt-2">
                <Button variant="secondary" className="me-2" onClick={onCancel} disabled={loading}>
                    Cancelar
                </Button>
                <Button variant="success" type="submit" disabled={loading}>
                    {loading ? <Spinner size="sm" animation="border" /> : <><FaSave className="me-1"/> Salvar</>}
                </Button>
            </div>
        </Form>
    );
};

export default OcorrenciasEdit;
